import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface WorkProcessStepProps {
  step: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
  isCompleted: boolean;
}

const WorkProcessStep: React.FC<WorkProcessStepProps> = ({
  step,
  title,
  description,
  icon,
  isActive,
  onClick,
  isCompleted
}) => {
  return (
    <motion.div
      className={`relative flex items-start gap-4 cursor-pointer group`}
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: step * 0.1 }}
      viewport={{ once: true }}
      onClick={onClick}
      whileHover={{ x: 5 }}
    >
      {/* Connector Line */}
      {step < 10 && (
        <div className="absolute left-6 top-10 w-0.5 h-16 bg-gray-200 z-0">
          <motion.div
            className="absolute top-0 left-0 w-full bg-gradient-to-b from-primary to-secondary"
            initial={{ height: 0 }}
            animate={isCompleted ? { height: '100%' } : { height: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          />
        </div>
      )}

      {/* Step Number and Icon */}
      <motion.div
        className={`relative z-10 flex-shrink-0 w-12 h-12 rounded-full border-2 flex items-center justify-center ${
          isActive
            ? 'bg-gradient-to-r from-primary to-secondary border-white text-white shadow-lg'
            : isCompleted
            ? 'bg-gradient-to-r from-primary/70 to-secondary/70 border-white/80 text-white'
            : 'bg-white border-gray-200 text-gray-400'
        }`}
        whileHover={{ scale: 1.1 }}
        animate={isActive ? { scale: 1.1, y: 0 } : { scale: 1, y: 0 }}
      >
        {isCompleted && !isActive ? (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <span>{icon}</span>
        )}
      </motion.div>

      {/* Content */}
      <div className="pt-1">
        <motion.h3
          className={`text-lg font-semibold mb-1 ${
            isActive ? 'text-primary' : 'text-gray-700 group-hover:text-primary'
          } transition-colors`}
        >
          {title}
        </motion.h3>
        <AnimatePresence>
          {isActive && (
            <motion.p
              className="text-gray-600 max-w-md"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              {description}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const WorkProcess: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);

  const processSteps = [
    {
      title: "Language Class N5 Level",
      description: "Begin your journey with our comprehensive Japanese language classes. Master JLPT N5 basics including essential vocabulary, grammar, and conversational skills.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
        </svg>
      )
    },
    {
      title: "Application",
      description: "Our team assists you with selecting the right schools and completing the application process, ensuring all documents are properly prepared and submitted.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      title: "Interview Selection",
      description: "We prepare you for successful interviews with Japanese language schools through mock sessions and personalized coaching on common questions and etiquette.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7a4 4 0 100 8 4 4 0 000-8zM16 7a4 4 0 100 8 4 4 0 000-8zM3 15h.803a2 2 0 011.82 1.173l.1.2a2 2 0 001.82 1.173H14m2-4h.803a2 2 0 011.82 1.173l.1.2a2 2 0 001.82 1.173H21" />
        </svg>
      )
    },
    {
      title: "Documentation",
      description: "We guide you through the documentation process, helping gather and prepare all required papers for your educational journey in Japan.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      title: "COE",
      description: "We assist in obtaining your Certificate of Eligibility (COE), a crucial document issued by Japanese immigration that serves as the basis for your student visa.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    {
      title: "Visa Apply",
      description: "With your COE in hand, we guide you through the Japanese student visa application process, ensuring all requirements are met for successful approval.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
        </svg>
      )
    },
    {
      title: "Departure",
      description: "We provide comprehensive pre-departure orientation covering travel arrangements, what to pack, arrival procedures, and initial settlement in Japan.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
        </svg>
      )
    },
    {
      title: "Language School",
      description: "During your language program, we maintain regular contact to support your academic progress and help you adapt to life in Japan.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      )
    },
    {
      title: "Graduation",
      description: "Complete your language school program with improved Japanese proficiency, ready for the next steps in your educational or career journey in Japan.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
      )
    },
    {
      title: "Further Studies",
      description: "We assist with applications to Japanese universities or vocational schools, helping you continue your educational journey in your chosen field.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )
    }
  ];

  const autoAdvance = () => {
    const nextStep = (activeStep + 1) % processSteps.length;
    setActiveStep(nextStep);
  };

  React.useEffect(() => {
    const timer = setTimeout(autoAdvance, 4000);
    return () => clearTimeout(timer);
  }, [activeStep]);

  return (
    <section className="py-20 bg-slate-50 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div 
          className="absolute -top-40 -right-40 w-96 h-96 bg-primary/5 rounded-full blur-3xl"
          animate={{ 
            x: [0, 30],
            y: [0, -30]
          }}
          transition={{ 
            repeat: Infinity,
            repeatType: "reverse",
            duration: 20,
            ease: "easeInOut" 
          }}
        />
        
        <motion.div 
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"
          animate={{ 
            x: [0, -30],
            y: [0, 30]
          }}
          transition={{ 
            repeat: Infinity,
            repeatType: "reverse",
            duration: 15,
            ease: "easeInOut" 
          }}
        />
      </div>

      {/* Japanese pattern background */}
      <div className="absolute inset-0 overflow-hidden opacity-5 pattern-japanese">
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          <motion.span
            className="text-sm font-medium px-4 py-1.5 rounded-full bg-primary/10 text-primary inline-block mb-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
          >
            How It Works
          </motion.span>
          <h2 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-black via-black to-black/80 mb-6">
            Your <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">Journey</span> to Japan
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg">
            Overview of our comprehensive process from language preparation to graduation and beyond
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Process Steps - Left Side */}
          <div className="space-y-16">
            {processSteps.slice(0, 5).map((step, index) => (
              <WorkProcessStep
                key={index}
                step={index}
                title={step.title}
                description={step.description}
                icon={step.icon}
                isActive={activeStep === index}
                onClick={() => setActiveStep(index)}
                isCompleted={index < activeStep}
              />
            ))}
          </div>

          {/* Interactive Process Visualization - Center */}
          <div className="relative">
            <motion.div
              className="h-full w-full flex items-center justify-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
            >
              <div className="bg-slate-800 px-6 py-8 rounded-2xl border border-white/10 w-full max-w-md shadow-xl">
                <div className="flex flex-col items-center">
                  <motion.div
                    className="w-24 h-24 mb-6 rounded-full flex items-center justify-center bg-gradient-to-r from-primary to-secondary text-white"
                    animate={{ 
                      scale: [1, 1.05, 1],
                      rotate: [0, 2, 0, -2, 0], 
                    }}
                    transition={{ 
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    {processSteps[activeStep].icon}
                  </motion.div>
                  
                  <motion.h3 
                    className="text-2xl font-bold text-white mb-3"
                    key={activeStep}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.5 }}
                  >
                    {processSteps[activeStep].title}
                  </motion.h3>
                  
                  <motion.p 
                    className="text-gray-300 text-center"
                    key={`desc-${activeStep}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    {processSteps[activeStep].description}
                  </motion.p>
                  
                  <div className="flex justify-center mt-8 space-x-2">
                    {processSteps.map((_, index) => (
                      <motion.button
                        key={index}
                        className={`w-2 h-2 rounded-full transition-all ${
                          index === activeStep 
                            ? 'w-8 bg-gradient-to-r from-primary to-secondary' 
                            : 'bg-white/30'
                        }`}
                        onClick={() => setActiveStep(index)}
                        whileHover={{ scale: 1.2 }}
                      />
                    ))}
                  </div>
                </div>
                
                <div className="mt-8 pt-8 border-t border-white/20">
                  <div className="grid grid-cols-2 gap-4">
                    <motion.div 
                      className="bg-white/10 p-4 rounded-lg"
                      whileHover={{ y: -5, backgroundColor: 'rgba(255, 255, 255, 0.15)' }}
                    >
                      <h4 className="font-semibold text-white mb-1 flex items-center">
                        <svg className="w-4 h-4 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Free Language Class
                      </h4>
                      <p className="text-gray-300 text-sm">N5 level training included with application</p>
                    </motion.div>
                    
                    <motion.div 
                      className="bg-white/10 p-4 rounded-lg"
                      whileHover={{ y: -5, backgroundColor: 'rgba(255, 255, 255, 0.15)' }}
                    >
                      <h4 className="font-semibold text-white mb-1 flex items-center">
                        <svg className="w-4 h-4 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Free Processing
                      </h4>
                      <p className="text-gray-300 text-sm">No application & processing fees</p>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Process Steps - Right Side */}
          <div className="space-y-16">
            {processSteps.slice(5).map((step, index) => (
              <WorkProcessStep
                key={index + 5}
                step={index + 5}
                title={step.title}
                description={step.description}
                icon={step.icon}
                isActive={activeStep === index + 5}
                onClick={() => setActiveStep(index + 5)}
                isCompleted={index + 5 < activeStep}
              />
            ))}
          </div>
        </div>
        
        <motion.div 
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <motion.a 
            href="/contact" 
            className="btn-primary inline-flex items-center gap-2 relative overflow-hidden group"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="relative z-10">Start Your Journey Today</span>
            <svg className="w-5 h-5 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
            <motion.span 
              className="absolute inset-0 bg-gradient-to-r from-primary via-secondary to-primary"
              initial={{ x: "-100%" }}
              whileHover={{ x: "100%" }}
              transition={{ duration: 1 }}
            />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default WorkProcess; 